// src/config/roleMenus.js
// Role-specific menu configurations

import { MENU_ITEMS, SECTIONS } from './navConfig';

// Installer (web) menu - for owners/managers
export const INSTALLER_MENU = {
  [SECTIONS.WORK]: [
    MENU_ITEMS.leads,
    MENU_ITEMS.requestimates,
    MENU_ITEMS.scheduled,
    MENU_ITEMS.inProgress,
    MENU_ITEMS.pickupDeliver
  ],
  [SECTIONS.COMPLETED]: [
    MENU_ITEMS.calendar,
    MENU_ITEMS.history,
    MENU_ITEMS.financials,
    MENU_ITEMS.time
  ],
  [SECTIONS.REFERENCE]: [
    MENU_ITEMS.customers,
    MENU_ITEMS.vehicles,
    MENU_ITEMS.services,
    MENU_ITEMS.inventory,
    MENU_ITEMS.staff
  ],
  [SECTIONS.SYSTEM]: [
    MENU_ITEMS.automations,
    MENU_ITEMS.userManagement,
    MENU_ITEMS.profile,
    MENU_ITEMS.systemHelp
  ]
};

// Dealer (web) menu
export const DEALER_MENU = {
  [SECTIONS.WORK]: [
    MENU_ITEMS.referrals,
    MENU_ITEMS.requests,
    MENU_ITEMS.scheduled,
    MENU_ITEMS.inProgress,
    MENU_ITEMS.pickups,
    MENU_ITEMS.reviews
  ],
  [SECTIONS.COMPLETED]: [
    MENU_ITEMS.financials
  ],
  [SECTIONS.REFERENCE]: [
    MENU_ITEMS.staff
  ],
  [SECTIONS.SYSTEM]: [
    MENU_ITEMS.systemHelp
  ]
};

// Technician (mobile) menu - simplified
export const TECHNICIAN_MENU = {
  [SECTIONS.WORK]: [
    MENU_ITEMS.myWork,
    MENU_ITEMS.pickupDeliver,
    MENU_ITEMS.leads
  ],
  [SECTIONS.SYSTEM]: [
    MENU_ITEMS.systemHelp
  ]
};

/**
 * Deep clone a menu object
 */
function cloneMenu(menu) {
  const cloned = {};
  for (const section in menu) {
    cloned[section] = menu[section].map(item => ({ ...item }));
  }
  return cloned;
}

/**
 * Filter installer menu based on access level and user role
 * @param {string} access - Legacy access level ('ADMIN', 'STANDARD', 'DIRECT')
 * @param {string} userRole - Unified role ('admin', 'manager', 'technician', 'viewer')
 */
function filterInstallerMenu(access, userRole) {
  const menu = cloneMenu(INSTALLER_MENU);

  // Check if user has admin privileges (either legacy or unified role)
  const isAdmin = access === 'ADMIN' || userRole === 'admin';

  // History only for ADMIN
  if (!isAdmin) {
    menu[SECTIONS.COMPLETED] = menu[SECTIONS.COMPLETED].filter(
      item => item.id !== 'history'
    );
  }

  // Inventory only for ADMIN
  if (!isAdmin) {
    menu[SECTIONS.REFERENCE] = menu[SECTIONS.REFERENCE].filter(
      item => item.id !== 'inventory'
    );
  }

  // User Management only for ADMIN
  if (!isAdmin) {
    menu[SECTIONS.SYSTEM] = menu[SECTIONS.SYSTEM].filter(
      item => item.id !== 'userManagement'
    );
  }

  return menu;
}

/**
 * Filter dealer menu based on access level
 */
function filterDealerMenu(access) {
  const menu = cloneMenu(DEALER_MENU);

  // No referrals for DIRECT access
  if (access === 'DIRECT') {
    menu[SECTIONS.WORK] = menu[SECTIONS.WORK].filter(
      item => item.id !== 'referrals'
    );
  }

  // Staff visible for all dealers (no filtering)

  return menu;
}

/**
 * Filter technician menu based on settings
 */
function filterTechnicianMenu(access, settings) {
  const menu = cloneMenu(TECHNICIAN_MENU);

  // Hide Leads if technicians don't action leads
  if (!settings.technicianCanActionLeads) {
    menu[SECTIONS.WORK] = menu[SECTIONS.WORK].filter(
      item => item.id !== 'leads'
    );
  }

  return menu;
}

/**
 * Get menu for a specific role with access-based filtering
 * @param {string} userType - 'installer' or 'dealer'
 * @param {string} userRole - 'owner', 'manager', 'technician', or null (legacy)
 * @param {string} access - 'ADMIN', 'STANDARD', 'DIRECT'
 * @param {object} settings - Company-specific settings
 * @returns {object} Filtered menu by section
 */
export function getMenuForRole(userType, userRole, access, settings = {}) {
  // Technician uses simplified menu
  if (userType === 'installer' && userRole === 'technician') {
    return filterTechnicianMenu(access, settings);
  }

  // Installer (owner/manager/legacy)
  if (userType === 'installer') {
    return filterInstallerMenu(access, userRole);
  }

  // Dealer
  if (userType === 'dealer') {
    return filterDealerMenu(access);
  }

  return {};
}
